if (!customElements.get('product-modal')) {
  class PinchZoom {
    constructor(img, container, signal) {
      if (!img || !container) return;
  
      container.style.touchAction = 'none';
  
      let initialDistance = null;
      let currentScale = 1;
      let startScale = 1;
      let initialPan = { x: 0, y: 0 };
      let currentPan = { x: 0, y: 0 };
      let startPan = { x: 0, y: 0 };
      
      const applyTransform = () => {
        // Clamp panning so the image doesn't leave the viewport
        const maxPanX = Math.max(0, (img.clientWidth * currentScale - container.clientWidth) / 2);
        const maxPanY = Math.max(0, (img.clientHeight * currentScale - container.clientHeight) / 2);
  
        currentPan.x = Math.max(-maxPanX, Math.min(maxPanX, currentPan.x));
        currentPan.y = Math.max(-maxPanY, Math.min(maxPanY, currentPan.y));
  
        img.style.transform = `translate(${currentPan.x}px, ${currentPan.y}px) scale(${currentScale})`;
        img.style.transformOrigin = 'center center';
        img.style.transition = 'none';
        img.style.willChange = 'transform';
      };
  
      const resetTransform = () => {
        currentScale = 1;
        currentPan = { x: 0, y: 0 };
        img.style.transition = 'transform 0.2s ease-out';
        img.style.transform = '';
        setTimeout(() => { 
          if (img) {
            img.style.transition = 'none'; 
            img.style.willChange = 'auto';
          }
        }, 200);
      };
  
      // --- Touch Events (Mobile Pinch/Pan) ---
      container.addEventListener('touchstart', (e) => {
        if (e.touches.length === 2) {
          initialDistance = Math.hypot(
            e.touches[0].pageX - e.touches[1].pageX,
            e.touches[0].pageY - e.touches[1].pageY
          );
          startScale = currentScale;
        } else if (e.touches.length === 1 && currentScale > 1) {
          initialPan = { x: e.touches[0].pageX, y: e.touches[0].pageY };
          startPan = { ...currentPan };
        }
      }, { signal, passive: false });
  
      container.addEventListener('touchmove', (e) => {
        if (e.touches.length === 2 && initialDistance) {
          e.preventDefault(); 
          const currentDistance = Math.hypot(
            e.touches[0].pageX - e.touches[1].pageX,
            e.touches[0].pageY - e.touches[1].pageY
          );
          const ratio = currentDistance / initialDistance;
          currentScale = Math.max(1, Math.min(startScale * ratio, 5));
          applyTransform();
        } else if (e.touches.length === 1 && currentScale > 1) {
          e.preventDefault();
          const dx = e.touches[0].pageX - initialPan.x;
          const dy = e.touches[0].pageY - initialPan.y;
          currentPan.x = startPan.x + dx;
          currentPan.y = startPan.y + dy;
          applyTransform();
        }
      }, { signal, passive: false });
  
      container.addEventListener('touchend', (e) => {
        if (e.touches.length < 2) initialDistance = null;
        if (e.touches.length === 0 && currentScale <= 1.05) resetTransform();
      }, { signal });
  
      // --- Wheel Events (Desktop Trackpad Pinch / Mouse Scroll) ---
      container.addEventListener('wheel', (e) => {
        e.preventDefault();
        if (e.ctrlKey || e.metaKey) {
          // Trackpad pinch
          const zoomAmount = e.deltaY * -0.01;
          currentScale = Math.max(1, Math.min(currentScale + zoomAmount, 5));
        } else {
          if (currentScale > 1) {
            // Pan if zoomed
            currentPan.x -= e.deltaX;
            currentPan.y -= e.deltaY;
          } else if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
            // Mouse wheel scroll to zoom
            const zoomAmount = e.deltaY * -0.002;
            currentScale = Math.max(1, Math.min(currentScale + zoomAmount, 5));
          }
        }
  
        if (currentScale <= 1.05 && currentScale !== 1) {
          resetTransform();
        } else {
          applyTransform();
        }
      }, { signal, passive: false });
  
      // --- Mouse Events (Desktop Click & Drag Pan) ---
      let isMouseDown = false;
      container.addEventListener('mousedown', (e) => {
        if (currentScale > 1) {
          isMouseDown = true;
          initialPan = { x: e.pageX, y: e.pageY };
          startPan = { ...currentPan };
          img.style.cursor = 'grabbing';
          e.preventDefault();
        }
      }, { signal });
      
      window.addEventListener('mousemove', (e) => {
        if (isMouseDown && currentScale > 1) {
          e.preventDefault();
          const dx = e.pageX - initialPan.x;
          const dy = e.pageY - initialPan.y;
          currentPan.x = startPan.x + dx;
          currentPan.y = startPan.y + dy;
          applyTransform();
        }
      }, { signal });
      
      window.addEventListener('mouseup', () => {
        if (isMouseDown) {
          isMouseDown = false;
          img.style.cursor = '';
        }
      }, { signal });

      // Stop pointerup from bubbling and closing the modal when zoomed in
      container.addEventListener('pointerup', (e) => {
        if (currentScale > 1) {
          e.stopPropagation();
        }
      }, { signal });
      
      // --- UI Buttons ---
      const zoomControls = container.parentElement.querySelector('.product-media-modal__zoom-controls');
      if (zoomControls) {
        zoomControls.classList.remove('hidden');
        
        const zoomInBtn = zoomControls.querySelector('.zoom-in');
        const zoomOutBtn = zoomControls.querySelector('.zoom-out');
        
        if (zoomInBtn) {
          zoomInBtn.addEventListener('click', () => {
            currentScale = Math.min(currentScale + 0.5, 5);
            applyTransform();
          }, { signal });
          zoomInBtn.addEventListener('pointerup', (e) => e.stopPropagation(), { signal });
        }
        
        if (zoomOutBtn) {
          zoomOutBtn.addEventListener('click', () => {
            if (currentScale > 1) {
              currentScale = Math.max(currentScale - 0.5, 1);
              if (currentScale === 1) {
                resetTransform();
              } else {
                applyTransform();
              }
            }
          }, { signal });
          zoomOutBtn.addEventListener('pointerup', (e) => e.stopPropagation(), { signal });
        }
      }
    }
  }

  class ProductModal extends ModalDialog {
      constructor() {
        super();
      }

      hide() {
        super.hide();
        const container = this.querySelector('.active-modal-media-container');
        if (container) container.innerHTML = '';
        const zoomControls = this.querySelector('.product-media-modal__zoom-controls');
        if (zoomControls) zoomControls.classList.add('hidden');
        if (this.zoomAbortController) {
          this.zoomAbortController.abort();
          this.zoomAbortController = null;
        }
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        const mediaId = this.openedBy.getAttribute('data-media-id');
        const template = this.querySelector(`template[data-media-id="${mediaId}"]`);
        const container = this.querySelector('.active-modal-media-container');

        if (!template || !container) return;

        // Inject the selected media template
        container.innerHTML = '';
        container.appendChild(template.content.cloneNode(true));

        const activeMedia = container.firstElementChild;
        
        const spinner = this.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.remove('hidden');

        if (activeMedia) {
          activeMedia.classList.add('active', 'media-loading');
        }

        const finalizeLoad = () => {
          if (spinner) spinner.classList.add('hidden');
          if (activeMedia) activeMedia.classList.remove('media-loading');
        };

        // Initialize pinch zoom for images
        const deferredMedia = container.querySelector('deferred-media');
        const img = container.querySelector('img');
        const isPureImage = img && !deferredMedia;

        if (this.zoomAbortController) this.zoomAbortController.abort();

        if (isPureImage) {
          this.zoomAbortController = new AbortController();
          new PinchZoom(img, container, this.zoomAbortController.signal);
        } else {
          container.style.touchAction = '';
        }

        if (img) {
          if (img.complete) {
            finalizeLoad();
          } else {
            img.addEventListener('load', finalizeLoad, { once: true });
            img.addEventListener('error', finalizeLoad, { once: true });
          }
        } else {
          finalizeLoad();
        }

        // Load deferred media if necessary
        if (deferredMedia && !deferredMedia.getAttribute('loaded')) {
          deferredMedia.loadContent(false);
        }
      }
  }

  customElements.define('product-modal', ProductModal);
}
